import { useCallback, useEffect, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useVersion } from 'react-admin';
import { Box, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { SnackbarContainer } from 'components/standalone';
import { extractErrorMessage } from 'utils';
import { SupplierContainer, SupplierFormValues } from '../containers';
import { SupplierForm } from '../components';

const SuppliersEditInner = () => {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const { notify } = SnackbarContainer.useContainer();
  const {
    selectedSupplier,
    isLoading,
    supplierLoaded,
    supplierError,
    formLoading,
    supplierRegions,
    supplierRepresentatives,
    supplierRegionsLoaded,
    supplierRepresentativesLoaded,
    regionsError,
    representativesError,
    loadSupplierById,
    updateSupplier,
    loadRegionOptions,
    loadUserRepresentativeOptions,
    loadSupplierRegionsForSupplier,
    loadSupplierRepresentativesForSupplier,
    setFormLoading,
    resetLoadedFlags,
  } = SupplierContainer.useContainer();

  const version = useVersion();

  const supplierId = selectedSupplier?.id;

  useEffect(() => {
    if (!id) return;
    loadSupplierById(id);
  }, [id, version, loadSupplierById]);

  useEffect(() => {
    if (!supplierId) return;
    resetLoadedFlags();
    loadSupplierRegionsForSupplier(supplierId);
    loadSupplierRepresentativesForSupplier(supplierId);
  }, [
    supplierId,
    resetLoadedFlags,
    loadSupplierRegionsForSupplier,
    loadSupplierRepresentativesForSupplier,
  ]);

  useEffect(() => {
    if (supplierError || (supplierLoaded && !selectedSupplier)) {
      notify('Supplier not found', 'error');
      history.push('/supplier');
    }
  }, [supplierError, supplierLoaded, selectedSupplier, notify, history]);

  const initialRegionIds = useMemo(
    () => supplierRegions.map(sr => sr.regionId),
    [supplierRegions],
  );

  const initialRepresentativeIds = useMemo(
    () => supplierRepresentatives.map(sr => sr.userId),
    [supplierRepresentatives],
  );

  const regionsLoading = !!supplierId && !supplierRegionsLoaded;
  const representativesLoading = !!supplierId && !supplierRepresentativesLoaded;

  const handleSubmit = useCallback(
    async (values: SupplierFormValues) => {
      if (!id) return;

      setFormLoading(true);
      try {
        // Single transactional request: fields + desired region and
        // representative sets, diffed server-side.
        await updateSupplier(id, values);

        notify('Element updated');
        history.push('/supplier');
      } catch (error: unknown) {
        console.error('Supplier update failed:', error);
        notify(
          extractErrorMessage(error, 'Error while updating the supplier'),
          'error',
        );
      } finally {
        setFormLoading(false);
      }
    },
    [id, updateSupplier, history, notify, setFormLoading],
  );

  const handleGoBack = useCallback(() => {
    history.push('/supplier');
  }, [history]);

  if (isLoading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  if (!selectedSupplier) {
    return null;
  }

  return (
    <Box className={classes.formContainer}>
      <SupplierForm
        mode="edit"
        initialValues={selectedSupplier}
        initialRegionIds={initialRegionIds}
        initialRepresentativeIds={initialRepresentativeIds}
        onSubmit={handleSubmit}
        // Saving with a half-loaded region/representative list would send
        // an incomplete desired set and unlink relations, so the form stays
        // disabled until both lists are in.
        loading={formLoading || regionsLoading || representativesLoading}
        onGoBack={handleGoBack}
        loadRegionOptions={loadRegionOptions}
        loadUserRepresentativeOptions={loadUserRepresentativeOptions}
        regionsLoading={regionsLoading}
        representativesLoading={representativesLoading}
        regionsError={regionsError}
        representativesError={representativesError}
      />
    </Box>
  );
};

export const SuppliersEdit = () => (
  <SupplierContainer.Provider>
    <SuppliersEditInner />
  </SupplierContainer.Provider>
);

const useStyles = makeStyles(theme => ({
  formContainer: {
    position: 'relative',
    boxShadow: theme.shadows[2],
    backgroundColor: theme.palette.background.paper,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4),
  },
}));

export default SuppliersEdit;
